import { Box, Stack } from '@mui/joy';
import { Plus } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { IPartFinderAttributeValue } from 'companySettings/types';
import { Button, ColorPickerField, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { getEmptyErrorMessage } from 'core/yup';
import { Direction, Variant } from 'theme/enums';

import { DEFAULT_COLOR_HEX, HEX_COLOR_PATTERN, VALUE_MAX_LENGTH } from './constants';

interface IProps {
  onAdd: (value: IPartFinderAttributeValue) => boolean;
}

// A custom colour: a name plus the colour itself, picked on the swatch or typed as a hex code.
export const ColourEditor = ({ onAdd }: IProps) => {
  const { t } = useTranslation();

  const { control, errors, getValues, setValue, setError, clearErrors, watch } = useFormContext();

  const colorHex: string = watch('newColorHex');

  const onAddColour = () => {
    const name = (getValues('newColorName') ?? '').trim();
    const hex = (getValues('newColorHex') ?? '').trim().toUpperCase();

    if (!name) {
      setError('newColorName', {
        type: 'required',
        message: getEmptyErrorMessage(t('labelColorName')),
      });

      return;
    }

    if (name.length > VALUE_MAX_LENGTH) {
      setError('newColorName', {
        type: 'maxLength',
        message: t('errorFieldMaxLength', { field: t('labelColorName'), length: VALUE_MAX_LENGTH }),
      });

      return;
    }

    if (!HEX_COLOR_PATTERN.test(hex)) {
      setError('newColorHex', {
        type: 'validate',
        message: t('errorPartFinderAttributeColorHexInvalid'),
      });

      return;
    }

    if (!onAdd({ value: name, colorHex: hex })) {
      setError('newColorName', {
        type: 'validate',
        message: t('errorPartFinderAttributeValueExists'),
      });

      return;
    }

    clearErrors(['newColorName', 'newColorHex']);
    setValue('newColorName', '');
  };

  return (
    <Stack gap={1}>
      <FormField
        name="newColorName"
        type={InputType.TEXT_INPUT}
        inputProps={{
          control,
          error: errors?.newColorName,
          showFieldError: true,
          label: t('labelColorName'),
        }}
      />
      <Stack direction={Direction.ROW} gap={1} alignItems="flex-start">
        <ColorPickerField
          value={HEX_COLOR_PATTERN.test(colorHex ?? '') ? colorHex : DEFAULT_COLOR_HEX}
          onChange={(hex) => setValue('newColorHex', hex.toUpperCase())}
        />
        <Box sx={{ flexGrow: 1 }}>
          <FormField
            name="newColorHex"
            type={InputType.TEXT_INPUT}
            inputProps={{
              control,
              error: errors?.newColorHex,
              showFieldError: true,
              placeholder: DEFAULT_COLOR_HEX,
            }}
          />
        </Box>
        <Button
          text={t('buttonLabelAdd')}
          variant={Variant.SOFT}
          startDecorator={<Plus />}
          onClick={onAddColour}
        />
      </Stack>
    </Stack>
  );
};
